import React from 'react';
import { alertApiRef, useApi } from '@backstage/core-plugin-api';
import { useObservable } from 'react-use';
import { Snackbar, IconButton, Paper, Typography, Box } from '@material-ui/core';
import { makeStyles } from '@material-ui/core/styles';
import CloseIcon from '@material-ui/icons/Close';
import ErrorOutlineIcon from '@material-ui/icons/ErrorOutline';
import WarningIcon from '@material-ui/icons/Warning';
import InfoIcon from '@material-ui/icons/Info';
import CheckCircleOutlineIcon from '@material-ui/icons/CheckCircleOutline';

const useStyles = makeStyles((theme) => ({
  root: {
    maxWidth: 380,
    minWidth: 280,
  },
  paper: {
    display: 'flex',
    alignItems: 'flex-start',
    padding: theme.spacing(2),
    borderRadius: 12,
    boxShadow: '0 8px 32px rgba(0,0,0,0.12)',
  },
  error: {
    backgroundColor: '#FEF2F2',
    borderLeft: '4px solid #DC2626',
  },
  warning: {
    backgroundColor: '#FFFBEB',
    borderLeft: '4px solid #F59E0B',
  },
  info: {
    backgroundColor: '#EFF6FF',
    borderLeft: '4px solid #3B82F6',
  },
  success: {
    backgroundColor: '#F0FDF4',
    borderLeft: '4px solid #22C55E',
  },
  icon: {
    marginRight: theme.spacing(1.5),
    marginTop: 2,
  },
  errorIcon: {
    color: '#DC2626',
  },
  warningIcon: {
    color: '#F59E0B',
  },
  infoIcon: {
    color: '#3B82F6',
  },
  successIcon: {
    color: '#22C55E',
  },
  content: {
    flex: 1,
    minWidth: 0,
  },
  title: {
    fontWeight: 600,
    fontSize: '0.95rem',
    marginBottom: theme.spacing(0.5),
    color: '#1F2937',
  },
  message: {
    fontSize: '0.875rem',
    color: '#4B5563',
    wordBreak: 'break-word',
    lineHeight: 1.5,
  },
  closeButton: {
    padding: 4,
    marginLeft: theme.spacing(1),
    marginTop: -4,
    marginRight: -8,
    color: '#9CA3AF',
    '&:hover': {
      backgroundColor: 'rgba(0,0,0,0.04)',
    },
  },
}));

/**
 * Parsea el mensaje de error para hacerlo más legible
 */
const parseErrorMessage = (message: string): { title: string; description: string } => {
  // Si es JSON, intentar parsearlo
  if (message.startsWith('{') || message.startsWith('[')) {
    try {
      const parsed = JSON.parse(message);
      if (parsed.error?.name && parsed.error?.message) {
        return {
          title: getErrorTitle(parsed.error.name),
          description: parsed.error.message || 'Ha ocurrido un error inesperado.',
        };
      }
      if (parsed.message) {
        return {
          title: 'Error',
          description: parsed.message,
        };
      }
    } catch {
      // No es JSON válido, continuar
    }
  }

  // Si contiene "NotAllowedError" o "Unauthorized"
  if (message.includes('NotAllowedError') || message.includes('Unauthorized')) {
    return {
      title: 'Acceso Denegado',
      description: 'No tienes permisos para esta acción.',
    };
  }

  // Si contiene "NotFoundError"
  if (message.includes('NotFoundError') || message.includes('not found')) {
    return {
      title: 'No Encontrado',
      description: 'El recurso no existe.',
    };
  }

  // Si contiene "NetworkError" o "fetch"
  if (message.includes('NetworkError') || message.includes('Failed to fetch')) {
    return {
      title: 'Error de Conexión',
      description: 'Verifica tu conexión a internet.',
    };
  }

  // Si contiene stack trace, limpiarlo
  if (message.includes('\\n') || message.includes('\n at ')) {
    const cleanMessage = message.split('\\n')[0].split('\n')[0];
    return {
      title: 'Error',
      description: cleanMessage.length > 80 
        ? cleanMessage.substring(0, 80) + '...' 
        : cleanMessage,
    };
  }

  // Si el mensaje es muy largo, truncarlo
  if (message.length > 80) {
    return {
      title: 'Error',
      description: message.substring(0, 80) + '...',
    };
  }

  return {
    title: message,
    description: '',
  };
};

const getErrorTitle = (errorName: string): string => {
  const titles: Record<string, string> = {
    NotAllowedError: 'Access Denied',
    NotFoundError: 'Not Found',
    AuthenticationError: 'Authentication Error',
    InputError: 'Invalid Data',
    ConflictError: 'Conflict',
    ServiceUnavailableError: 'Service Unavailable',
    NotImplementedError: 'Feature Not Available',
  };
  return titles[errorName] || 'Error';
};

const getIcon = (severity: string, classes: ReturnType<typeof useStyles>) => {
  switch (severity) {
    case 'error':
      return <ErrorOutlineIcon className={`${classes.icon} ${classes.errorIcon}`} />;
    case 'warning':
      return <WarningIcon className={`${classes.icon} ${classes.warningIcon}`} />;
    case 'success':
      return <CheckCircleOutlineIcon className={`${classes.icon} ${classes.successIcon}`} />;
    default:
      return <InfoIcon className={`${classes.icon} ${classes.infoIcon}`} />;
  }
};

const getPaperClass = (severity: string, classes: ReturnType<typeof useStyles>) => {
  switch (severity) {
    case 'error':
      return `${classes.paper} ${classes.error}`;
    case 'warning':
      return `${classes.paper} ${classes.warning}`;
    case 'success':
      return `${classes.paper} ${classes.success}`;
    default:
      return `${classes.paper} ${classes.info}`;
  }
};

export interface CustomAlertDisplayProps {
  transientTimeoutMs?: number;
}

export const CustomAlertDisplay: React.FC<CustomAlertDisplayProps> = ({
  transientTimeoutMs = 5000,
}) => {
  const classes = useStyles();
  const alertApi = useApi(alertApiRef);
  const [open, setOpen] = React.useState(false);
  const [currentAlert, setCurrentAlert] = React.useState<{
    message: string;
    severity: 'error' | 'warning' | 'info' | 'success';
  } | null>(null);

  const alert$ = alertApi.alert$();
  const alert = useObservable(alert$);

  React.useEffect(() => {
    if (alert) {
      const severity = alert.severity || 'info';
      setCurrentAlert({
        message: typeof alert.message === 'string' ? alert.message : String(alert.message),
        severity,
      });
      setOpen(true);
    }
  }, [alert]);

  const handleClose = () => {
    setOpen(false);
  };

  if (!currentAlert) {
    return null;
  }

  const { title, description } = parseErrorMessage(currentAlert.message);

  return (
    <Snackbar
      open={open}
      autoHideDuration={currentAlert.severity === 'error' ? null : transientTimeoutMs}
      onClose={handleClose}
      anchorOrigin={{ vertical: 'top', horizontal: 'right' }}
      className={classes.root}
    >
      <Paper className={getPaperClass(currentAlert.severity, classes)} elevation={0}>
        {getIcon(currentAlert.severity, classes)}
        <Box className={classes.content}>
          <Typography className={classes.title}>{title}</Typography>
          <Typography className={classes.message}>{description}</Typography>
        </Box>
        <IconButton
          size="small"
          className={classes.closeButton}
          onClick={handleClose}
          aria-label="cerrar"
        >
          <CloseIcon fontSize="small" />
        </IconButton>
      </Paper>
    </Snackbar>
  );
};
